import axios from 'axios';
import { useState, useRef, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { 
  Camera, Upload, X, ZoomIn, Image, Video, FileText, 
  Users, Calendar, CheckSquare, Clock, Tag, Plus, Trash2
} from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { urlFichier } from '@/lib/fichiers';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

export function OngletPhotos({ dossier, onUpdate }) {
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [videoCreation, setVideoCreation] = useState(dossier?.video_creation || false);
  const [videoDebriefing, setVideoDebriefing] = useState(dossier?.video_debriefing || "");
  const [videoEmployes, setVideoEmployes] = useState(dossier?.video_employes_assignes || []);
  const [videoDatePrevue, setVideoDatePrevue] = useState(dossier?.video_date_prevue?.split('T')[0] || "");
  const [videoChecklist, setVideoChecklist] = useState(dossier?.video_checklist || []);
  const [videoDuree, setVideoDuree] = useState(dossier?.video_duree_estimee || "");
  const [videoTags, setVideoTags] = useState(dossier?.video_tags || []);
  const [newTag, setNewTag] = useState("");
  const [savingVideo, setSavingVideo] = useState(false);
  const [employees, setEmployees] = useState([]);
  const fileInputRef = useRef(null);

  const photos = dossier?.photos || [];

  // Charger les employés admin au montage
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/employees`);
        if (res.status === 200) {
          const adminEmployees = (res.data.employees || []).filter(
            e => e.type_employe === 'admin'
          );
          setEmployees(adminEmployees);
        }
      } catch (e) {
        console.error("Erreur chargement employés:", e);
      }
    };
    if (videoCreation) {
      fetchEmployees();
    }
  }, [videoCreation]);

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    let uploaded = 0;
    const { compressImageIfNeeded } = await import('@/lib/imageCompression');
    for (const file of files) {
      try {
        const compressed = await compressImageIfNeeded(file);
        const formData = new FormData();
        formData.append("file", compressed);
        const uploadRes = await axios.post(`${API_URL}/api/crm/upload-photo`, formData);
        const uploadData = uploadRes.data;
        if (!uploadData.photo_url) throw new Error("Upload échoué");

        const addRes = await axios.post(`${API_URL}/api/crm/dossiers/${dossier.id}/photos`, { photo_url: uploadData.photo_url });
        if (addRes.status !== 200) throw new Error("Ajout échoué");
        uploaded++;
      } catch (err) {
        toast.error(`Erreur upload: ${file.name}`);
      }
    }
    setUploading(false);
    if (uploaded > 0) {
      toast.success(`${uploaded} photo(s) ajoutée(s)`);
      onUpdate();
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleRemove = async (photoUrl) => {
    try {
      await axios.delete(`${API_URL}/api/crm/dossiers/${dossier.id}/photos`, {
        data: { photo_url: photoUrl }
      });
      toast.success("Photo supprimée");
      onUpdate();
    } catch {
      toast.error("Erreur suppression");
    }
  };

  const handleVideoCreationToggle = async (checked) => {
    setVideoCreation(checked);
    try {
      await saveVideoData(checked);
      toast.success(checked ? "Création vidéo activée" : "Création vidéo désactivée");
      onUpdate();
    } catch {
      toast.error("Erreur lors de la mise à jour");
      setVideoCreation(!checked);
    }
  };

  const saveVideoData = async (customVideoCreation = null) => {
    setSavingVideo(true);
    try {
      const payload = {
        video_creation: customVideoCreation !== null ? customVideoCreation : videoCreation,
        video_debriefing: videoDebriefing,
        video_employes_assignes: videoEmployes,
        video_date_prevue: videoDatePrevue ? new Date(videoDatePrevue).toISOString() : null,
        video_checklist: videoChecklist,
        video_duree_estimee: videoDuree ? parseInt(videoDuree) : null,
        video_tags: videoTags
      };

      await axios.patch(`${API_URL}/api/crm/dossiers/${dossier.id}/video-creation`, payload);
      toast.success("Informations vidéo sauvegardées");
      onUpdate();
    } catch {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSavingVideo(false);
    }
  };

  const toggleChecklistItem = (index) => {
    const newChecklist = [...videoChecklist];
    newChecklist[index].done = !newChecklist[index].done;
    setVideoChecklist(newChecklist);
  };

  const addChecklistItem = () => {
    setVideoChecklist([...videoChecklist, { label: "Nouvelle tâche", done: false }]);
  };

  const updateChecklistLabel = (index, newLabel) => {
    const newChecklist = [...videoChecklist];
    newChecklist[index].label = newLabel;
    setVideoChecklist(newChecklist);
  };

  const removeChecklistItem = (index) => {
    setVideoChecklist(videoChecklist.filter((_, i) => i !== index));
  };

  const addTag = () => {
    if (newTag.trim() && !videoTags.includes(newTag.trim())) {
      setVideoTags([...videoTags, newTag.trim()]);
      setNewTag("");
    }
  };

  const removeTag = (tagToRemove) => {
    setVideoTags(videoTags.filter(t => t !== tagToRemove));
  };

  const toggleEmployeeAssignment = (empId) => {
    if (videoEmployes.includes(empId)) {
      setVideoEmployes(videoEmployes.filter(id => id !== empId));
    } else {
      setVideoEmployes([...videoEmployes, empId]);
    }
  };

  return (
    <div className="space-y-6">
      {/* Section 1: Photos Avant/Après */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5 text-blue-600" />
              Photos Marketing & Vidéos (Avant/Après)
              <span className="text-sm font-normal text-slate-500">({photos.length} photo{photos.length > 1 ? "s" : ""})</span>
            </CardTitle>
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={handleFileChange}
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                size="sm"
              >
                <Upload className="w-4 h-4 mr-1" />
                {uploading ? "Envoi..." : "Ajouter photos"}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {photos.length === 0 ? (
            <div
              className="border-2 border-dashed border-slate-200 rounded-xl p-12 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              <Image className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">Aucune photo pour ce projet</p>
              <p className="text-sm text-slate-400 mt-1">Cliquez ici ou utilisez le bouton "Ajouter photos" pour uploader des photos avant/après</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {photos.map((url, idx) => (
                <div key={url.id || url._id || idx} className="relative group rounded-lg overflow-hidden border border-slate-200 aspect-square">
                  <img
                    src={urlFichier(url)}
                    alt={`Photo ${idx + 1}`}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.src = "https://via.placeholder.com/150?text=Photo"; }}
                  />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      className="h-7 w-7 p-0"
                      onClick={() => setPreviewUrl(url.startsWith("/api") ? `${API_URL}${url}` : url)}
                    >
                      <ZoomIn className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      className="h-7 w-7 p-0"
                      onClick={() => handleRemove(url)}
                    >
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs text-center py-0.5">
                    Photo {idx + 1}
                  </div>
                </div>
              ))}
              <div
                className="border-2 border-dashed border-slate-200 rounded-lg aspect-square flex flex-col items-center justify-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="w-6 h-6 text-slate-400 mb-1" />
                <span className="text-xs text-slate-500">Ajouter</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Section 2: Création Vidéo / Débriefing */}
      <Card className="border-purple-200">
        <CardHeader className="bg-gradient-to-r from-purple-50 to-blue-50">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Video className="w-5 h-5 text-purple-600" />
              Création Vidéo & Débriefing
            </CardTitle>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="video-creation"
                checked={videoCreation}
                onCheckedChange={handleVideoCreationToggle}
              />
              <Label
                htmlFor="video-creation"
                className="text-sm font-medium cursor-pointer select-none"
              >
                Création vidéo sur ce contrat
              </Label>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-4">
          {videoCreation ? (
            <div className="space-y-6">
              {/* Débriefing */}
              <div>
                <Label htmlFor="video-debriefing" className="flex items-center gap-2 mb-2">
                  <FileText className="w-4 h-4 text-slate-600" />
                  Notes et idées pour le contenu vidéo
                </Label>
                <Textarea
                  id="video-debriefing"
                  placeholder="Écrivez vos idées pour la vidéo, les points à mentionner, les angles de prise de vue, le storytelling, etc..."
                  value={videoDebriefing}
                  onChange={(e) => setVideoDebriefing(e.target.value)}
                  className="min-h-[120px] resize-y"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Exemple : "Montrer l'avant/après de la cuisine, insister sur la transformation, ajouter un témoignage client, durée 60 secondes"
                </p>
              </div>

              {/* Employés assignés */}
              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <Users className="w-4 h-4 text-slate-600" />
                  Employés admin assignés à la création vidéo
                </Label>
                <div className="flex flex-wrap gap-2">
                  {employees.map(emp => (
                    <Badge
                      key={emp.id}
                      variant={videoEmployes.includes(emp.id) ? "default" : "outline"}
                      className="cursor-pointer"
                      onClick={() => toggleEmployeeAssignment(emp.id)}
                    >
                      {emp.prenom} {emp.nom}
                      {videoEmployes.includes(emp.id) && <X className="w-3 h-3 ml-1" />}
                    </Badge>
                  ))}
                  {employees.length === 0 && (
                    <p className="text-sm text-slate-500">Aucun employé admin disponible</p>
                  )}
                </div>
              </div>

              {/* Date prévue */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="video-date" className="flex items-center gap-2 mb-2">
                    <Calendar className="w-4 h-4 text-slate-600" />
                    Date prévue du tournage
                  </Label>
                  <Input
                    id="video-date"
                    type="date"
                    value={videoDatePrevue}
                    onChange={(e) => setVideoDatePrevue(e.target.value)}
                  />
                </div>

                {/* Durée estimée */}
                <div>
                  <Label htmlFor="video-duree" className="flex items-center gap-2 mb-2">
                    <Clock className="w-4 h-4 text-slate-600" />
                    Durée estimée (secondes)
                  </Label>
                  <Input
                    id="video-duree"
                    type="number"
                    placeholder="60"
                    value={videoDuree}
                    onChange={(e) => setVideoDuree(e.target.value)}
                  />
                </div>
              </div>

              {/* Tags */}
              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <Tag className="w-4 h-4 text-slate-600" />
                  Tags
                </Label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {videoTags.map((tag, idx) => (
                    <Badge key={idx} variant="secondary" className="gap-1">
                      {tag}
                      <X
                        className="w-3 h-3 cursor-pointer hover:text-red-600"
                        onClick={() => removeTag(tag)}
                      />
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    placeholder="Ex: Avant/Après, Témoignage, Drone..."
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addTag()}
                  />
                  <Button size="sm" onClick={addTag}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Checklist */}
              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <CheckSquare className="w-4 h-4 text-slate-600" />
                  Checklist de production
                </Label>
                <div className="space-y-2 mb-3">
                  {videoChecklist.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 border rounded-lg hover:bg-slate-50">
                      <Checkbox
                        checked={item.done}
                        onCheckedChange={() => toggleChecklistItem(idx)}
                      />
                      <Input
                        value={item.label}
                        onChange={(e) => updateChecklistLabel(idx, e.target.value)}
                        className="flex-1 h-8"
                      />
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => removeChecklistItem(idx)}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
                <Button size="sm" variant="outline" onClick={addChecklistItem}>
                  <Plus className="w-4 h-4 mr-1" /> Ajouter une tâche
                </Button>
              </div>

              {/* Bouton sauvegarde */}
              <div className="flex justify-end pt-4 border-t">
                <Button
                  onClick={() => saveVideoData()}
                  disabled={savingVideo}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {savingVideo ? "Sauvegarde..." : "Sauvegarder toutes les informations"}
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <Video className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="font-medium">Aucune création vidéo prévue pour ce projet</p>
              <p className="text-sm mt-1">Cochez "Création vidéo sur ce contrat" pour activer cette section</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Fullscreen preview dialog */}
      <Dialog open={!!previewUrl} onOpenChange={() => setPreviewUrl(null)}>
        <DialogContent className="max-w-4xl p-2">
          {previewUrl && (
            <img
              src={previewUrl}
              alt="Aperçu"
              className="w-full h-auto max-h-[80vh] object-contain rounded"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default OngletPhotos;
